import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  WarningCircle,
  CheckCircle,
  XCircle,
  MagnifyingGlass,
  ArrowCounterClockwise,
  PencilSimple,
} from '@phosphor-icons/react';
import { apiClient, MERCHANT_ROUTES, useNotificationStore } from '@dukene/shared';
import { ProductsSubNav } from './products/Products';

interface InventoryItem {
  id: string;
  sku: string;
  quantity: number;
  lowStockThreshold: number;
  product: {
    id: string;
    title?: string;
    name?: string;
    sku: string;
    images?: { url: string }[];
  };
}

interface ProductInventory {
  id: string;
  title: string;
  sku: string;
  image?: string;
  variants: {
    id: string;
    title: string;
    sku: string;
    inventoryQuantity: number;
    available: boolean;
  }[];
  totalQuantity: number;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
}

export default function Inventory() {
  const [products, setProducts] = useState<ProductInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState<number>(0);
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<any>('/inventory');
      const inventoryItems: InventoryItem[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
        ? response.data
        : [];

      const groupedProducts: Record<string, ProductInventory> = {};

      inventoryItems.forEach((item) => {
        if (!item.product) return;
        const prodTitle = item.product.title || item.product.name || 'Unnamed SKU';

        if (!groupedProducts[item.product.id]) {
          groupedProducts[item.product.id] = {
            id: item.product.id,
            title: prodTitle,
            sku: item.product.sku || item.sku,
            image: item.product.images?.[0]?.url,
            variants: [],
            totalQuantity: 0,
            status: 'IN_STOCK',
          };
        }

        const product = groupedProducts[item.product.id];

        product.variants.push({
          id: item.id,
          title: item.sku,
          sku: item.sku,
          inventoryQuantity: item.quantity,
          available: item.quantity > 0,
        });

        product.totalQuantity += item.quantity;
      });

      const processedProducts = Object.values(groupedProducts).map((product) => {
        let status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' = 'IN_STOCK';
        if (product.totalQuantity === 0) {
          status = 'OUT_OF_STOCK';
        } else if (product.totalQuantity < 10) {
          status = 'LOW_STOCK';
        }
        return { ...product, status };
      });

      setProducts(processedProducts);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to fetch inventory data. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdjust = async (variantId: string, newQty: number) => {
    try {
      await apiClient.patch(`/inventory/${variantId}`, { quantity: newQty });
      addNotification({
        type: 'success',
        title: 'Updated',
        message: 'Stock quantity adjusted successfully.',
      });
      setEditingId(null);
      fetchInventory();
    } catch {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Could not update inventory quantity.',
      });
    }
  };

  const filteredProducts = products.filter((p) => {
    const matchesQuery =
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesQuery && matchesStatus;
  });

  const totalSKUs = products.length;
  const inStockCount = products.filter((p) => p.status === 'IN_STOCK').length;
  const lowStockCount = products.filter((p) => p.status === 'LOW_STOCK').length;
  const outOfStockCount = products.filter((p) => p.status === 'OUT_OF_STOCK').length;

  return (
    <div className="space-y-5">
      <ProductsSubNav />

      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Inventory Management
          </h1>
        </div>
        <button
          type="button"
          onClick={fetchInventory}
          disabled={loading}
          className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors shrink-0"
        >
          <ArrowCounterClockwise weight="bold" className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh Counts
        </button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Total SKUs</span>
            <Package weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">{totalSKUs}</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">In Stock</span>
            <CheckCircle weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#006838] font-mono mt-1">{inStockCount}</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Low Stock</span>
            <WarningCircle weight="fill" className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-xl font-bold text-amber-600 font-mono mt-1">{lowStockCount}</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Out of Stock</span>
            <XCircle weight="fill" className="w-4 h-4 text-red-600" />
          </div>
          <p className="text-xl font-bold text-red-600 font-mono mt-1">{outOfStockCount}</p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2 relative">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
            />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
            />
          </div>

          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="all">All Inventory Status</option>
              <option value="IN_STOCK">In Stock</option>
              <option value="LOW_STOCK">Low Stock</option>
              <option value="OUT_OF_STOCK">Out of Stock</option>
            </select>
          </div>
        </div>

        {/* Inventory Directory Table */}
        {loading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-14 px-6">
            <Package weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-2" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Inventory Items Match
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">
              Try clearing filters or checking that your products have quantity tracking enabled.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Product / Variant</th>
                  <th className="py-2.5 px-4">SKU</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-right">Available Stock</th>
                  <th className="py-2.5 px-4 text-right">Quick Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {filteredProducts.map((p) => {
                  const firstVariant = p.variants[0];
                  return (
                    <tr
                      key={p.id}
                      className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {p.image ? (
                            <img
                              src={p.image}
                              alt={p.title}
                              className="w-10 h-10 object-cover rounded-lg border border-[#E1E4E8]"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#5C5F62]">
                              <Package weight="fill" className="w-5 h-5" />
                            </div>
                          )}
                          <div>
                            <Link
                              to={`${MERCHANT_ROUTES.PRODUCTS}/${p.id}/edit`}
                              className="font-semibold text-[#202223] dark:text-white hover:text-[#006838]"
                            >
                              {p.title}
                            </Link>
                          </div>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <code className="bg-[#F6F6F7] dark:bg-slate-800 px-2 py-0.5 rounded font-mono text-[11px] font-bold text-[#202223] dark:text-slate-200">
                          {p.sku || 'N/A'}
                        </code>
                      </td>

                      <td className="py-3 px-4">
                        {p.status === 'IN_STOCK' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
                            <CheckCircle weight="fill" className="w-3 h-3" />
                            In Stock
                          </span>
                        ) : p.status === 'LOW_STOCK' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            <WarningCircle weight="fill" className="w-3 h-3" />
                            Low Stock
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                            <XCircle weight="fill" className="w-3 h-3" />
                            Out of Stock
                          </span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right font-mono font-bold text-[#202223] dark:text-white">
                        {editingId === firstVariant?.id ? (
                          <input
                            type="number"
                            value={editQty}
                            onChange={(e) => setEditQty(Number(e.target.value))}
                            className="w-20 h-7 text-right px-2 border border-[#006838] rounded bg-white dark:bg-slate-800 font-mono text-xs"
                            autoFocus
                          />
                        ) : (
                          <span>{p.totalQuantity}</span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right">
                        {firstVariant ? (
                          editingId === firstVariant.id ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleQuickAdjust(firstVariant.id, editQty)}
                                className="h-7 px-2.5 rounded bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                className="h-7 px-2 rounded border border-[#D2D5D9] text-[#5C5F62] text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(firstVariant.id);
                                setEditQty(firstVariant.inventoryQuantity);
                              }}
                              className="h-7 px-2.5 rounded border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1"
                            >
                              <PencilSimple weight="bold" className="w-3.5 h-3.5" />
                              Adjust
                            </button>
                          )
                        ) : (
                          <span className="text-[#8C9196] text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
